import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

export default function Users() {
  const {
    user,
    users,
    createUser,
    updateUser,
    deleteUser,
    toggleUserStatus,
    changeUserPassword,
    loadUsers,
    apiRequest,
    updateCompany,
  } = useAuth();

  const [activeTab, setActiveTab] = useState('company-users');
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [showCreateCompany, setShowCreateCompany] = useState(false);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const [deleteOptions, setDeleteOptions] = useState({
    deleteDocuments: false,
    anonymizeDocuments: false,
  });

  // Form states
  const [newCompany, setNewCompany] = useState({
    name: '',
    displayName: '',
    description: '',
    address: '',
    contactEmail: '',
    contactPhone: '',
    website: '',
    maxUsers: 50,
    maxDocuments: 1000,
    storageLimitGb: 10,
  });

  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    email: '',
    firstName: '',
    lastName: '',
    role: 'user',
    companyId: null,
  });

  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: '',
  });

  // 1. State für Bearbeiten-Modal
  const [editUser, setEditUser] = useState(null);

  // 1. State für Bearbeiten-Modal Firma
  const [editCompany, setEditCompany] = useState(null);
  const [availableModels, setAvailableModels] = useState([]);

  const loadCompanies = useCallback(async () => {
    try {
      const response = await apiRequest('/companies');
      if (response.success) {
        setCompanies(response.companies);
      }
    } catch (error) {
      toast.error('Fehler beim Laden der Firmen');
    }
  }, [apiRequest]);

  const loadAvailableModels = useCallback(async () => {
    try {
      const response = await apiRequest('/companies/ai-models/available');
      if (response.success) {
        setAvailableModels(response.models);
      }
    } catch (error) {
      toast.error('Fehler beim Laden der verfügbaren KI-Modelle');
    }
  }, [apiRequest]);

  const loadCompanyUsers = useCallback(
    async (companyId) => {
      try {
        const response = await apiRequest(`/companies/${companyId}/users`);
        if (response.success) {
          setCompanyUsers(response.users);
        }
      } catch (error) {
        toast.error('Fehler beim Laden der Firmen-Benutzer');
      }
    },
    [apiRequest],
  );

  const loadUserCompany = useCallback(
    async (companyId) => {
      try {
        const response = await apiRequest(`/companies/${companyId}`);
        if (response.success) {
          setSelectedCompany(response.company);
          setActiveTab('company-users');
        }
      } catch (error) {
        toast.error('Fehler beim Laden der Firma');
      }
    },
    [apiRequest],
  );

  useEffect(() => {
    const loadUserData = async () => {
      if (user) {
        if (user.role === 'dachadmin') {
          setActiveTab('companies');
          loadCompanies();
          loadUsers();
          loadAvailableModels();
        } else {
          setActiveTab('company-users');
          if (user.companyId) {
            loadCompanyUsers(user.companyId);
            loadUserCompany(user.companyId);
          } else {
            const userWithCompany = await apiRequest(`/auth/users/${user.id}`);
            if (userWithCompany.success && userWithCompany.user.companyId) {
              loadCompanyUsers(userWithCompany.user.companyId);
              loadUserCompany(userWithCompany.user.companyId);
            }
          }
        }
      }
    };
    loadUserData();
  }, [user]); // ✅ Nur user als Dependency - die Funktionen sind bereits memoized

  // Tab-Definitionen basierend auf Benutzerrolle
  const tabs =
    user?.role === 'dachadmin'
      ? [
          { id: 'companies', name: 'Firmen-Übersicht', icon: '🏢' },
          { id: 'users', name: 'Alle Benutzer', icon: '👥' },
          {
            id: 'company-users',
            name: 'Firmen-Benutzer',
            icon: '🏢👤',
            disabled: !selectedCompany,
          },
        ]
      : [{ id: 'company-users', name: 'Meine Firmen-Benutzer', icon: '🏢👤', disabled: false }];

  const handleCreateCompany = async (e) => {
    e.preventDefault();

    try {
      const response = await apiRequest('/companies', {
        method: 'POST',
        body: JSON.stringify(newCompany),
      });

      if (response.success) {
        toast.success('Firma erfolgreich erstellt');
        setNewCompany({
          name: '',
          displayName: '',
          description: '',
          address: '',
          contactEmail: '',
          contactPhone: '',
          website: '',
          maxUsers: 50,
          maxDocuments: 1000,
          storageLimitGb: 10,
        });
        setShowCreateCompany(false);
        loadCompanies();
      } else {
        toast.error('Fehler beim Erstellen der Firma');
      }
    } catch (error) {
      toast.error('Fehler beim Erstellen der Firma');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (newUser.password.length < 6) {
      toast.error('Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }

    const result = await createUser(newUser);
    if (result.success) {
      setNewUser({
        username: '',
        password: '',
        email: '',
        firstName: '',
        lastName: '',
        role: 'user',
        companyId: user?.role === 'dachadmin' ? null : selectedCompany?.id || null,
      });
      setShowCreateUser(false);
      // Lade die Benutzerliste der Firma neu
      if (selectedCompany) {
        loadCompanyUsers(selectedCompany.id);
      }
    }
  };

  const handleChangePassword = async (userId) => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Passwörter stimmen nicht überein');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error('Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }

    const result = await changeUserPassword(userId, passwordData.newPassword);
    if (result.success) {
      setPasswordData({ newPassword: '', confirmPassword: '' });
      setShowPasswordModal(null);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    setShowDeleteModal({ userId, userName });
  };

  const confirmDeleteUser = async () => {
    if (!showDeleteModal) return;

    const { userId } = showDeleteModal;
    const result = await deleteUser(userId, deleteOptions);

    if (result.success) {
      setShowDeleteModal(null);
      setDeleteOptions({ deleteDocuments: false, anonymizeDocuments: false });
    }
  };

  const handleCompanySelect = (company) => {
    setSelectedCompany(company);
    setActiveTab('company-users');
    loadCompanyUsers(company.id);
  };

  // Helper function to get user display name
  const getUserDisplayName = (user) => {
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    if (user.firstName) return user.firstName;
    if (user.lastName) return user.lastName;
    return user.username || user.email || 'Unbekannter Benutzer';
  };

  // Helper function to get user initials
  const getUserInitials = (user) => {
    const displayName = getUserDisplayName(user);
    const parts = displayName.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return displayName.charAt(0).toUpperCase();
  };

  // 2. Handler zum Öffnen des Bearbeiten-Modals
  const handleEditUser = (user) => {
    setEditUser({ ...user });
    setShowCreateUser(false);
  };

  // 3. Handler zum Speichern der Änderungen
  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!editUser) return;
    // API-Call zum Aktualisieren des Benutzers
    await updateUser(editUser.id, editUser);
    setEditUser(null);
    await loadUsers();
  };

  // 2. Handler zum Öffnen des Bearbeiten-Modals für Firmen
  const handleEditCompany = (company) => {
    setEditCompany({ ...company });
    setShowCreateCompany(false);
  };

  // 3. Handler zum Speichern der Änderungen an einer Firma
  const handleUpdateCompany = async (e) => {
    e.preventDefault();
    if (!editCompany) return;

    try {
      // API-Call zum Aktualisieren der Firma
      await updateCompany(editCompany.id, editCompany);

      // Wenn KI-Modell geändert wurde, speichere es separat
      if (editCompany.defaultAiModel && user?.role === 'dachadmin') {
        try {
          await apiRequest(`/companies/${editCompany.id}/ai-model`, {
            method: 'PUT',
            body: JSON.stringify({ model: editCompany.defaultAiModel }),
          });

          toast.success('Firma und KI-Modell erfolgreich aktualisiert');
        } catch (aiModelError) {
          console.error('Fehler beim Speichern des KI-Modells:', aiModelError);
          toast.error('Firma aktualisiert, aber KI-Modell konnte nicht gespeichert werden');
        }
      } else {
        toast.success('Firma erfolgreich aktualisiert');
      }

      setEditCompany(null);
      await loadCompanies();
    } catch (error) {
      console.error('Fehler beim Aktualisieren der Firma:', error);
      toast.error('Fehler beim Aktualisieren der Firma');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Benutzer & Firmen-Verwaltung
          </h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Verwalten Sie Firmen und deren Benutzer
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 dark:border-gray-700 mb-8">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => !tab.disabled && setActiveTab(tab.id)}
                disabled={tab.disabled}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                  activeTab === tab.id
                    ? 'border-primary text-primary'
                    : tab.disabled
                      ? 'border-transparent text-gray-300 dark:text-gray-600 cursor-not-allowed'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {/* Firmen-Übersicht - Nur für Dachadmin */}
          {activeTab === 'companies' && user?.role === 'dachadmin' && (
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Firmen-Übersicht
                </h3>
                <button
                  onClick={() => setShowCreateCompany(true)}
                  className="bg-primary hover:bg-primary-700 text-white px-4 py-2 rounded-md text-sm font-medium"
                >
                  Neue Firma
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {companies.map((company) => (
                  <div
                    key={company.id}
                    className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => handleCompanySelect(company)}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-900 dark:text-white">
                        {company.displayName || company.name}
                      </h4>
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          company.isActive
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                            : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                        }`}
                      >
                        {company.isActive ? 'Aktiv' : 'Inaktiv'}
                      </span>
                    </div>

                    {company.description && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                        {company.description}
                      </p>
                    )}

                    <div className="space-y-1 text-sm text-gray-500 dark:text-gray-400">
                      <div className="flex justify-between">
                        <span>Benutzer:</span>
                        <span>
                          {company.userCount} / {company.maxUsers}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Max. Dokumente:</span>
                        <span>{company.maxDocuments.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Speicher-Limit:</span>
                        <span>{company.storageLimitGb} GB</span>
                      </div>
                      <div className="flex justify-between">
                        <span>KI-Modell:</span>
                        <span className="text-xs">
                          {availableModels[company.defaultAiModel]?.name ||
                            company.defaultAiModel ||
                            'Standard'}
                        </span>
                      </div>
                    </div>
                    {user?.role === 'dachadmin' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditCompany(company);
                        }}
                        className="text-primary hover:underline ml-2"
                      >
                        Bearbeiten
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Alle Benutzer - Nur für Dachadmin */}
          {activeTab === 'users' && user?.role === 'dachadmin' && (
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Alle Benutzer</h3>
                {(user?.role === 'admin' ||
                  user?.role === 'superadmin' ||
                  user?.role === 'dachadmin') && (
                  <button
                    onClick={() => setShowCreateUser(true)}
                    className="bg-primary hover:bg-primary-700 text-white px-4 py-2 rounded-md text-sm font-medium"
                  >
                    Neuer Benutzer
                  </button>
                )}
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Benutzer
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Firma
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Rolle
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Aktionen
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {users.map((tableUser) => (
                      <tr key={tableUser.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-primary flex items-center justify-center">
                                <span className="text-sm font-medium text-white">
                                  {getUserInitials(tableUser)}
                                </span>
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {getUserDisplayName(tableUser)}
                              </div>
                              <div className="text-sm text-gray-500 dark:text-gray-400">
                                {tableUser.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {tableUser.companyName || 'Keine Firma'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              tableUser.role === 'dachadmin'
                                ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400'
                                : tableUser.role === 'superadmin'
                                  ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                                  : tableUser.role === 'admin'
                                    ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400'
                                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                            }`}
                          >
                            {tableUser.role === 'dachadmin'
                              ? 'Dachadministrator'
                              : tableUser.role === 'superadmin'
                                ? 'Superadministrator'
                                : tableUser.role === 'admin'
                                  ? 'Administrator'
                                  : 'Benutzer'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              tableUser.isActive
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                                : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                            }`}
                          >
                            {tableUser.isActive ? 'Aktiv' : 'Inaktiv'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={() => setShowPasswordModal(tableUser.id)}
                              className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                            >
                              Passwort ändern
                            </button>
                            <button
                              onClick={() => toggleUserStatus(tableUser.id)}
                              className="text-yellow-600 hover:text-yellow-900 dark:text-yellow-400 dark:hover:text-yellow-300"
                            >
                              {tableUser.isActive ? 'Deaktivieren' : 'Aktivieren'}
                            </button>
                            {user?.id !== tableUser.id && (
                              <button
                                onClick={() =>
                                  handleDeleteUser(tableUser.id, getUserDisplayName(tableUser))
                                }
                                className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                              >
                                Löschen
                              </button>
                            )}
                            <button
                              onClick={() => handleEditUser(tableUser)}
                              className="text-primary hover:underline"
                            >
                              Bearbeiten
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Firmen-Benutzer */}
          {activeTab === 'company-users' && selectedCompany && (
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Benutzer von {selectedCompany.displayName || selectedCompany.name}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {companyUsers.length} / {selectedCompany.maxUsers} Benutzer
                  </p>
                </div>
                <div className="flex space-x-3">
                  {/* Benutzer erstellen Button für alle berechtigten Rollen */}
                  {(user?.role === 'admin' ||
                    user?.role === 'superadmin' ||
                    user?.role === 'dachadmin') && (
                    <button
                      onClick={() => {
                        setNewUser((prev) => ({ ...prev, companyId: selectedCompany.id }));
                        setShowCreateUser(true);
                      }}
                      className="bg-primary hover:bg-primary-700 text-white px-4 py-2 rounded-md text-sm font-medium"
                    >
                      Neuer Benutzer
                    </button>
                  )}
                  {/* Zurück Button nur für Dachadmin */}
                  {user?.role === 'dachadmin' && (
                    <button
                      onClick={() => setActiveTab('companies')}
                      className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                    >
                      ← Zurück zu Firmen
                    </button>
                  )}
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Benutzer
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Rolle
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Letzter Login
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {companyUsers.map((user) => (
                      <tr key={user.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-primary flex items-center justify-center">
                                <span className="text-sm font-medium text-white">
                                  {getUserInitials(user)}
                                </span>
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {getUserDisplayName(user)}
                              </div>
                              <div className="text-sm text-gray-500 dark:text-gray-400">
                                {user.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              user.role === 'dachadmin'
                                ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400'
                                : user.role === 'superadmin'
                                  ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                                  : user.role === 'admin'
                                    ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400'
                                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                            }`}
                          >
                            {user.role === 'dachadmin'
                              ? 'Dachadministrator'
                              : user.role === 'superadmin'
                                ? 'Superadministrator'
                                : user.role === 'admin'
                                  ? 'Administrator'
                                  : 'Benutzer'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              user.isActive
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                                : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                            }`}
                          >
                            {user.isActive ? 'Aktiv' : 'Inaktiv'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {user.lastLogin
                            ? new Date(user.lastLogin).toLocaleDateString('de-DE')
                            : 'Nie'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Modals */}
        {/* Neue Firma Modal */}
        {showCreateCompany && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Neue Firma erstellen
              </h3>
              <form onSubmit={handleCreateCompany} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Firmenname *
                    </label>
                    <input
                      type="text"
                      value={newCompany.name}
                      onChange={(e) => setNewCompany((prev) => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Anzeigename
                    </label>
                    <input
                      type="text"
                      value={newCompany.displayName}
                      onChange={(e) =>
                        setNewCompany((prev) => ({ ...prev, displayName: e.target.value }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Beschreibung
                  </label>
                  <textarea
                    value={newCompany.description}
                    onChange={(e) =>
                      setNewCompany((prev) => ({ ...prev, description: e.target.value }))
                    }
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Max. Benutzer
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="1000"
                      value={newCompany.maxUsers}
                      onChange={(e) =>
                        setNewCompany((prev) => ({ ...prev, maxUsers: parseInt(e.target.value) }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Max. Dokumente
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={newCompany.maxDocuments}
                      onChange={(e) =>
                        setNewCompany((prev) => ({
                          ...prev,
                          maxDocuments: parseInt(e.target.value),
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Speicher-Limit (GB)
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={newCompany.storageLimitGb}
                      onChange={(e) =>
                        setNewCompany((prev) => ({
                          ...prev,
                          storageLimitGb: parseInt(e.target.value),
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateCompany(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
                  >
                    Abbrechen
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary-700"
                  >
                    Firma erstellen
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Neuer Benutzer Modal */}
        {showCreateUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Neuen Benutzer erstellen
              </h3>
              <form onSubmit={handleCreateUser} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Benutzername *
                  </label>
                  <input
                    type="text"
                    value={newUser.username}
                    onChange={(e) => setNewUser((prev) => ({ ...prev, username: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    E-Mail *
                  </label>
                  <input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser((prev) => ({ ...prev, email: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Vorname
                    </label>
                    <input
                      type="text"
                      value={newUser.firstName}
                      onChange={(e) =>
                        setNewUser((prev) => ({ ...prev, firstName: e.target.value }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Nachname
                    </label>
                    <input
                      type="text"
                      value={newUser.lastName}
                      onChange={(e) =>
                        setNewUser((prev) => ({ ...prev, lastName: e.target.value }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Passwort *
                  </label>
                  <input
                    type="password"
                    value={newUser.password}
                    onChange={(e) => setNewUser((prev) => ({ ...prev, password: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Rolle
                    </label>
                    <select
                      value={newUser.role}
                      onChange={(e) => setNewUser((prev) => ({ ...prev, role: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="user">Benutzer</option>
                      {(user?.role === 'admin' ||
                        user?.role === 'superadmin' ||
                        user?.role === 'dachadmin') && <option value="admin">Administrator</option>}
                      {(user?.role === 'superadmin' || user?.role === 'dachadmin') && (
                        <option value="superadmin">Superadministrator</option>
                      )}
                      {user?.role === 'dachadmin' && (
                        <option value="dachadmin">Dachadministrator</option>
                      )}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Firma
                    </label>
                    {user?.role === 'dachadmin' ? (
                      <select
                        value={newUser.companyId || ''}
                        onChange={(e) =>
                          setNewUser((prev) => ({
                            ...prev,
                            companyId: e.target.value ? parseInt(e.target.value) : null,
                          }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="">Firma wählen...</option>
                        {companies.map((company) => (
                          <option key={company.id} value={company.id}>
                            {company.displayName || company.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={
                          selectedCompany?.displayName || selectedCompany?.name || 'Eigene Firma'
                        }
                        disabled
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-100 dark:bg-gray-600 text-gray-500 dark:text-gray-400"
                      />
                    )}
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateUser(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
                  >
                    Abbrechen
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary-700"
                  >
                    Benutzer erstellen
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Passwort ändern Modal */}
        {showPasswordModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Passwort ändern
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Neues Passwort
                  </label>
                  <input
                    type="password"
                    value={passwordData.newPassword}
                    onChange={(e) =>
                      setPasswordData((prev) => ({ ...prev, newPassword: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Passwort bestätigen
                  </label>
                  <input
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={(e) =>
                      setPasswordData((prev) => ({ ...prev, confirmPassword: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    onClick={() => setShowPasswordModal(null)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
                  >
                    Abbrechen
                  </button>
                  <button
                    onClick={() => handleChangePassword(showPasswordModal)}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary-700"
                  >
                    Ändern
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* DSGVO-Löschmodal */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-lg">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Benutzer DSGVO-konform löschen
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                Möchten Sie den Benutzer <strong>"{showDeleteModal.userName}"</strong> wirklich
                löschen?
                <br />
                <span className="text-red-600 dark:text-red-400">
                  Diese Aktion kann nicht rückgängig gemacht werden!
                </span>
              </p>

              <div className="space-y-4 mb-6">
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">
                    📊 Analytics-Daten
                  </h4>
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    Alle Nutzungsanalysen und Events werden automatisch gelöscht.
                  </p>
                </div>

                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-3">
                    📄 Dokumente behandeln
                  </h4>
                  <div className="space-y-3">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="documentOption"
                        value="keep"
                        checked={
                          !deleteOptions.deleteDocuments && !deleteOptions.anonymizeDocuments
                        }
                        onChange={() =>
                          setDeleteOptions({ deleteDocuments: false, anonymizeDocuments: false })
                        }
                        className="mr-3"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        Dokumente behalten (nicht empfohlen für DSGVO-Konformität)
                      </span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="documentOption"
                        value="anonymize"
                        checked={deleteOptions.anonymizeDocuments}
                        onChange={() =>
                          setDeleteOptions({ deleteDocuments: false, anonymizeDocuments: true })
                        }
                        className="mr-3"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        Dokumente anonymisieren (Benutzer-Zuordnung entfernen)
                      </span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="documentOption"
                        value="delete"
                        checked={deleteOptions.deleteDocuments}
                        onChange={() =>
                          setDeleteOptions({ deleteDocuments: true, anonymizeDocuments: false })
                        }
                        className="mr-3"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        Alle Dokumente löschen (vollständige DSGVO-Konformität)
                      </span>
                    </label>
                  </div>
                </div>

                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                  <h4 className="font-medium text-yellow-900 dark:text-yellow-100 mb-2">
                    ⚠️ Weitere Daten
                  </h4>
                  <p className="text-sm text-yellow-700 dark:text-yellow-300">
                    Sessions, Konversationen, API-Keys und RadMan-Sync-Daten werden automatisch
                    gelöscht.
                  </p>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(null);
                    setDeleteOptions({ deleteDocuments: false, anonymizeDocuments: false });
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  Abbrechen
                </button>
                <button
                  onClick={confirmDeleteUser}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
                >
                  Benutzer löschen
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Benutzer bearbeiten Modal */}
        {editUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Benutzer bearbeiten
              </h3>
              <form onSubmit={handleUpdateUser} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Benutzername *
                  </label>
                  <input
                    type="text"
                    value={editUser.username}
                    onChange={(e) => setEditUser((prev) => ({ ...prev, username: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    E-Mail *
                  </label>
                  <input
                    type="email"
                    value={editUser.email}
                    onChange={(e) => setEditUser((prev) => ({ ...prev, email: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Vorname
                    </label>
                    <input
                      type="text"
                      value={editUser.firstName}
                      onChange={(e) =>
                        setEditUser((prev) => ({ ...prev, firstName: e.target.value }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Nachname
                    </label>
                    <input
                      type="text"
                      value={editUser.lastName}
                      onChange={(e) =>
                        setEditUser((prev) => ({ ...prev, lastName: e.target.value }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Rolle
                  </label>
                  <select
                    value={editUser.role}
                    onChange={(e) => setEditUser((prev) => ({ ...prev, role: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="user">Benutzer</option>
                    {(user?.role === 'admin' ||
                      user?.role === 'superadmin' ||
                      user?.role === 'dachadmin') && <option value="admin">Administrator</option>}
                    {(user?.role === 'superadmin' || user?.role === 'dachadmin') && (
                      <option value="superadmin">Superadministrator</option>
                    )}
                    {user?.role === 'dachadmin' && (
                      <option value="dachadmin">Dachadministrator</option>
                    )}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Firma
                  </label>
                  {user?.role === 'dachadmin' ? (
                    <select
                      value={editUser.companyId || ''}
                      onChange={(e) =>
                        setEditUser((prev) => ({
                          ...prev,
                          companyId: e.target.value ? parseInt(e.target.value) : null,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="">Firma wählen...</option>
                      {companies.map((company) => (
                        <option key={company.id} value={company.id}>
                          {company.displayName || company.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={
                        selectedCompany?.displayName || selectedCompany?.name || 'Eigene Firma'
                      }
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-100 dark:bg-gray-600 text-gray-500 dark:text-gray-400"
                    />
                  )}
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setEditUser(null)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
                  >
                    Abbrechen
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary-700"
                  >
                    Speichern
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Firma bearbeiten Modal */}
        {editCompany && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Firma bearbeiten
              </h3>
              <form onSubmit={handleUpdateCompany} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Firmenname *
                  </label>
                  <input
                    type="text"
                    value={editCompany.name}
                    onChange={(e) => setEditCompany((prev) => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Anzeigename
                  </label>
                  <input
                    type="text"
                    value={editCompany.displayName}
                    onChange={(e) =>
                      setEditCompany((prev) => ({ ...prev, displayName: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Beschreibung
                  </label>
                  <textarea
                    value={editCompany.description}
                    onChange={(e) =>
                      setEditCompany((prev) => ({ ...prev, description: e.target.value }))
                    }
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    KI-Modell
                  </label>
                  <select
                    value={editCompany.defaultAiModel || 'gemini-2.5-flash'}
                    onChange={(e) =>
                      setEditCompany((prev) => ({ ...prev, defaultAiModel: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                  >
                    {Object.entries(availableModels || {}).map(([key, model]) => (
                      <option key={key} value={key}>
                        {model?.name || key}
                      </option>
                    ))}
                    {!availableModels[editCompany.defaultAiModel] && editCompany.defaultAiModel && (
                      <option value={editCompany.defaultAiModel}>
                        {editCompany.defaultAiModel}
                      </option>
                    )}
                  </select>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Max. Benutzer
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="1000"
                      value={editCompany.maxUsers}
                      onChange={(e) =>
                        setEditCompany((prev) => ({ ...prev, maxUsers: parseInt(e.target.value) }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Max. Dokumente
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={editCompany.maxDocuments}
                      onChange={(e) =>
                        setEditCompany((prev) => ({
                          ...prev,
                          maxDocuments: parseInt(e.target.value),
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Speicher-Limit (GB)
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={editCompany.storageLimitGb}
                      onChange={(e) =>
                        setEditCompany((prev) => ({
                          ...prev,
                          storageLimitGb: parseInt(e.target.value),
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setEditCompany(null)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
                  >
                    Abbrechen
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary-700"
                  >
                    Speichern
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
